import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCenterGroups } from '../../../../api/centers/center-groups'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'
import CenterGroupCircle from './center-group-circle'

type AssignCenterGroupInput = {
  groupId: number
}

type Props = BaseFormProps<AssignCenterGroupInput> & {
  className?: string
  centerId: number
}

const formSchema: ZodType<AssignCenterGroupInput> = z.object({
  groupId: createNumberSchema('Sous-groupe')
})

const AssignCenterGroupForm = ({
  className,
  centerId,
  defaultValues,
  ...validationButtonProps
}: Props) => {
  const form = useForm<AssignCenterGroupInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: centerGroups } = useQuery({
    queryKey: ['center-groups', centerId],
    queryFn: () => getCenterGroups(centerId),
    enabled: !!centerId
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-4', className)}>
          <FormField
            control={form.control}
            name="groupId"
            render={({ field }) => {
              const selectedGroup = centerGroups?.data.find((g) => g.id === field.value)
              return (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      dropDownPosition="above"
                      label="Sélectionner un sous-groupe"
                      options={
                        centerGroups?.data.map((group) => ({
                          label: group.name,
                          value: group.id,
                          custom: (
                            <div className="flex items-center gap-2">
                              <CenterGroupCircle size="small" color={group.color} />
                              {group.name}
                            </div>
                          )
                        })) ?? []
                      }
                      selectedOption={
                        selectedGroup
                          ? {
                              custom: (
                                <div className="flex items-center gap-2">
                                  <CenterGroupCircle size="small" color={selectedGroup.color} />
                                  {selectedGroup.name}
                                </div>
                              ),
                              value: field.value,
                              label: selectedGroup.name
                            }
                          : undefined
                      }
                    />
                  </FormControl>
                </FormItem>
              )
            }}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default AssignCenterGroupForm
